import React from 'react';
import * as yup from 'yup';
import { useForm, SubmitHandler } from 'react-hook-form';
import Typography from '@mui/material/Typography';
import { yupResolver } from '@hookform/resolvers/yup';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

import { logger } from '../../logging';
import { FORM_TITLES } from '../constants';
import { SubmissionButton } from '../../components';
import { jarvisApi } from '../../api';
import { useAuth } from '../../auth';
import {
  CarrierListField,
  FileNameField,
  NotifyEmailListField,
} from '../fields';
import { invoiceAuditFieldSchema } from '../invoiceAuditFieldSchema';
import { SubmissionSuccess } from '../SubmissionSuccess';

const schema = yup.object({
  ...invoiceAuditFieldSchema,
});

export type InvoiceAuditFormFields = yup.InferType<typeof schema>;

export const InvoiceAuditForm = () => {
  const auth = useAuth();
  const [submitting, setSubmitting] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const [generatedFileName, setGeneratedFileName] = React.useState<string>();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<InvoiceAuditFormFields>({
    resolver: yupResolver(schema),
    defaultValues: {
      fileName: '',
      targetedCarriers: [],
      notifyEmails: auth.user?.email ? [{ value: auth.user.email }] : [],
    },
    shouldUnregister: true,
  });

  const onSubmit: SubmitHandler<InvoiceAuditFormFields> = async (formData) => {
    setSubmitting(true);
    setShowError(false);

    try {
      // Call API to generate and upload invoice audit analysis
      const response = await jarvisApi.submitInvoiceAudit({
        fileName: formData.fileName,
        scacs: formData.targetedCarriers.map((c) => c.carrier.scac),
        notifyEmails: formData.notifyEmails.map((e) => e.value),
        userName: auth.user?.userName || '',
      });

      logger.info('Invoice Audit Analysis submitted successfully');
      setGeneratedFileName(response?.fileName);
      setShowSuccess(true);
    } catch (e: any) {
      logger.error('Invoice Audit Analysis submission failed', e);
      setShowError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setShowSuccess(false);
    setShowError(false);
    setGeneratedFileName(undefined);
    reset();
  };

  if (showSuccess) {
    return <SubmissionSuccess onReset={handleReset} fileName={generatedFileName} />;
  }

  return (
    <>
      <Typography
        align="center"
        component="h1"
        variant="h4"
        sx={{ mb: 3, mt: 2 }}
      >
        {FORM_TITLES.INVOICE_AUDIT_ANALYSIS || 'Invoice Audit Analysis Request'}
      </Typography>

      <form
        style={{ marginTop: '1rem', marginBottom: 0 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FileNameField<InvoiceAuditFormFields>
          control={control}
          name="fileName"
          error={errors.fileName}
        />

        <CarrierListField<InvoiceAuditFormFields>
          control={control}
          error={errors.targetedCarriers as any}
          label="Targeted Carriers (select up to 4)"
          name="targetedCarriers"
        />

        <NotifyEmailListField<InvoiceAuditFormFields>
          control={control}
          error={errors.notifyEmails}
          name="notifyEmails"
          hidden={!auth.isBGEmployee}
        />

        {Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <AlertTitle>Please fix the following errors:</AlertTitle>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {errors.fileName && <li>{errors.fileName.message}</li>}
              {errors.targetedCarriers && <li>{(errors.targetedCarriers as any).message}</li>}
              {errors.notifyEmails && <li>Valid notification email is required</li>}
            </ul>
          </Alert>
        )}

        {showError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            Submission failed. Please try again.
          </Alert>
        )}

        <SubmissionButton
          loading={submitting}
          buttonProps={{
            sx: { mt: 3, p: 2 },
            fullWidth: true,
            type: 'submit',
          }}
        />
      </form>
    </>
  );
};
